// ============================================================
// 8. StockAgingReportPage.tsx
// ============================================================
// src/features/reports/pages/StockAgingReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getStockAgingReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import { Clock, Package, DollarSign, AlertTriangle, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';

const agingColors: Record<string, { gradient: string; badge: string }> = {
    '0-30 days': { gradient: 'from-emerald-500 to-teal-600', badge: 'success' },
    '31-60 days': { gradient: 'from-blue-500 to-indigo-600', badge: 'info' },
    '61-90 days': { gradient: 'from-amber-500 to-orange-600', badge: 'warning' },
    '90+ days': { gradient: 'from-rose-500 to-pink-600', badge: 'danger' },
};

export function StockAgingReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [selectedBucket, setSelectedBucket] = useState<string | null>(null);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['report-stock-aging', tenantId, branchId],
        queryFn: () => getStockAgingReport(tenantId, branchId || undefined),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'stock_aging', format, { branchId });
        window.open(url, '_blank');
    };

    const hasFilters = !!(branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const buckets = data?.buckets || {};
    const bucketSummaries = data?.summary?.bucketSummaries || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <ReportHeader
                title="Stock Aging Report"
                description="Inventory age analysis"
                icon={Clock}
                iconGradient="from-violet-500 to-purple-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<Clock className="w-10 h-10" />}
                        title="No Aging Data"
                        description="No inventory aging data available."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <SummaryCard
                                label="Total Aged Items"
                                value={String(data.summary?.totalAgedItems || 0)}
                                icon={Package}
                                gradient="from-violet-500 to-purple-600"
                            />
                            <SummaryCard
                                label="Total Aged Value"
                                value={formatNaira(data.summary?.totalAgedValue || 0)}
                                icon={DollarSign}
                                gradient="from-slate-500 to-slate-600"
                            />
                            <SummaryCard
                                label="90+ Days Items"
                                value={String(buckets['90+ days']?.length || 0)}
                                icon={AlertTriangle}
                                gradient="from-rose-500 to-pink-600"
                            />
                        </div>

                        {/* Aging Buckets Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {bucketSummaries.map((bucket: any, i: number) => {
                                const colors = agingColors[bucket.bucket] || agingColors['0-30 days'];
                                const isSelected = selectedBucket === bucket.bucket;

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        whileHover={{ y: -3 }}
                                        onClick={() => setSelectedBucket(isSelected ? null : bucket.bucket)}
                                        className={`cursor-pointer bg-white rounded-2xl border-2 p-5 transition-all ${isSelected ? 'border-violet-500 shadow-lg' : 'border-slate-200 shadow-sm hover:shadow-md'
                                            }`}
                                    >
                                        <div className={`inline-flex p-2 rounded-xl bg-gradient-to-br ${colors.gradient} mb-3`}>
                                            <Clock className="w-5 h-5 text-white" />
                                        </div>
                                        <h4 className="text-sm font-bold text-slate-900">{bucket.bucket}</h4>
                                        <div className="mt-2 space-y-1">
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Items:</span>
                                                <span className="font-semibold">{bucket.count}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Quantity:</span>
                                                <span className="font-semibold">{bucket.totalQuantity.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-500">Value:</span>
                                                <span className="font-semibold">{formatNaira(bucket.totalCostValue)}</span>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Items Table for Selected Bucket */}
                        {selectedBucket && buckets[selectedBucket] && (
                            <Card className="overflow-hidden">
                                <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-slate-800">
                                        {selectedBucket} - {buckets[selectedBucket].length} items
                                    </h3>
                                    <Badge variant={agingColors[selectedBucket]?.badge as any || 'neutral'} size="sm">
                                        {buckets[selectedBucket].reduce((s: number, i: any) => s + parseFloat(i.totalCostValue), 0).toLocaleString()}
                                    </Badge>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Category</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Qty</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Days in Stock</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Cost Value</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Retail Value</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {buckets[selectedBucket].map((item: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.productName}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">{item.categoryName}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-semibold">{item.quantityOnHand}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge
                                                            variant={item.daysInStock > 90 ? 'danger' : item.daysInStock > 60 ? 'warning' : 'info'}
                                                            size="sm"
                                                        >
                                                            {item.daysInStock} days
                                                        </Badge>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(item.totalCostValue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNaira(item.totalRetailValue)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}